import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

class BundleBuilder {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.discount = Number(root.dataset.discount || 0) / 100;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.currency = root.dataset.currency || 'USD';
    this.checkboxes = Array.from(root.querySelectorAll('[data-bundle-item]'));
    this.totalEl = root.querySelector('[data-bundle-total]');
    this.rawEl = root.querySelector('[data-bundle-raw]');
    this.countEl = root.querySelector('[data-bundle-count]');
    this.addButton = root.querySelector('[data-bundle-add]');
    this.addButtonLabel = this.addButton?.textContent?.trim() ?? '';

    this.checkboxes.forEach((cb) => cb.addEventListener('change', () => this.update()));
    this.addButton?.addEventListener('click', () => this.addToCart());
    this.update();
  }

  get selected() {
    return this.checkboxes.filter((cb) => cb instanceof HTMLInputElement && cb.checked);
  }

  update() {
    const selected = this.selected;
    const raw = selected.reduce((sum, cb) => sum + Number(cb.dataset.price || 0), 0);
    const total = Math.round((raw * (1 - this.discount)) / 100) * 100;

    if (this.countEl) this.countEl.textContent = String(selected.length);
    if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat, this.currency);
    if (this.rawEl) {
      if (raw > total) {
        this.rawEl.textContent = formatMoney(raw, this.moneyFormat, this.currency);
        this.rawEl.hidden = false;
      } else {
        this.rawEl.hidden = true;
      }
    }
    if (this.addButton instanceof HTMLButtonElement) this.addButton.disabled = selected.length === 0;
  }

  async addToCart() {
    const selected = this.selected;
    if (!selected.length || !(this.addButton instanceof HTMLButtonElement)) return;

    this.addButton.disabled = true;
    this.addButton.textContent = this.addButton.dataset.loadingText || this.addButtonLabel;

    const items = selected.map((cb) => ({ id: Number(cb.dataset.variantId), quantity: 1 }));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (result.status) {
        throw new Error(result.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bundle-builder',
          sourceId: this.root.id || 'bundle-builder',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        },
      });
    } catch (error) {
      console.error('[bundle-builder]', error);
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
    } finally {
      this.addButton.textContent = this.addButtonLabel;
      this.addButton.disabled = this.selected.length === 0;
    }
  }
}

function init(scope = document) {
  scope.querySelectorAll('[data-bundle-builder]').forEach((root) => {
    if (root instanceof HTMLElement && !root.dataset.bundleBuilderInit) {
      root.dataset.bundleBuilderInit = 'true';
      new BundleBuilder(root);
    }
  });
}

init();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
